import { productStore } from './productStore';
import { inVersion } from '../productMock';
import type { VersionTeam } from './budget';

/** A percentage per version, like its rates: a new version copies it and a deleted one takes it. */
export const retentionKey = (productId: string, version?: string): string =>
  inVersion(`productRetention:${productId}`, version);

/** Money paid back into a sprint's bill, fixed at what was held when it was released. */
export const retentionReleaseKey = (productId: string, sprintId: string): string =>
  `productRetentionRelease:${productId}:${sprintId}`;

const asPercent = (value: unknown): number | null => {
  const amount = typeof value === 'string' ? Number(value.trim().replace(',', '.')) : value;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0 || amount > 100) return null;
  return Math.round(amount * 100) / 100;
};

/** What a typed percentage reads as: blank is none, and anything outside 0 to 100 is refused. */
export const typedPercent = (typed: string): number | null => (typed.trim() === '' ? 0 : asPercent(typed));

// Falls back to the unversioned key, the way every versioned value reads before its version was saved.
export const readRetention = (productId: string, version: string): number => {
  const raw = productStore.getItem(retentionKey(productId, version)) ?? productStore.getItem(retentionKey(productId));
  return (raw === null ? null : asPercent(raw)) ?? 0;
};

export const writeRetention = (productId: string, version: string, percent: number): void => {
  const key = retentionKey(productId, version);
  // Stored as 0 rather than removed, or the version would read the unversioned fallback again.
  productStore.setItem(key, String(percent));
};

export const readRelease = (productId: string, sprintId: string): number => {
  const raw = productStore.getItem(retentionReleaseKey(productId, sprintId));
  const amount = raw === null ? null : Number(raw);
  return amount !== null && Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) / 100 : 0;
};

export const writeRelease = (productId: string, sprintId: string, amount: number): void => {
  const key = retentionReleaseKey(productId, sprintId);
  if (amount > 0) productStore.setItem(key, String(amount));
  else productStore.removeItem(key);
};

/** The year's releases by sprint; a sprint nothing was released into is not a key. */
export const readYearReleases = (productId: string, sprintIds: string[]): Record<string, number> =>
  Object.fromEntries(
    sprintIds.map(sprintId => [sprintId, readRelease(productId, sprintId)] as const).filter(([, amount]) => amount > 0),
  );

/** Held back off a sprint's bill after its adjustment; a sprint that bills nothing holds nothing back. */
export const heldBack = (percent: number, net: number): number =>
  net > 0 && percent > 0 ? Math.round(net * percent) / 100 : 0;

/** The percentage of the version a sprint is priced under. */
export const retentionAt = (productId: string, team: VersionTeam | null): number =>
  team ? readRetention(productId, team.version.number) : 0;

/** The sprint's retention line as money off, like an adjustment: held back, less anything released into it. */
export const retentionLine = (productId: string, team: VersionTeam | null, sprintId: string, net: number): number =>
  Math.round((heldBack(retentionAt(productId, team), net) - readRelease(productId, sprintId)) * 100) / 100;
